/**
 * Bundle 127 - Incident actions panel.
 *
 * Discoverable UI for every /admin/incident/* recovery endpoint.
 * User policy: no hidden URLs. Every incident endpoint must have
 * a corresponding admin UI button here.
 *
 * Pattern: click Dry-run, review diff, click Apply. Both are
 * super_admin-gated on the backend.
 *
 * Endpoints covered (Bundle 108-126):
 *  - Bundle 108: /admin/incident/backfill-bundle107-reissue
 *  - Bundle 109: /admin/incident/consolidate-campaigns
 *  - Bundle 110: /admin/incident/dedupe-invitations
 *  - Bundle 112: /admin/incident/reset-stale-invitations
 *  - Bundle 116: /admin/incident/refresh-question-set
 *  - Bundle 117: /admin/incident/reset-registration-survey-state
 *  - Bundle 119: /admin/incident/reset-response-drift-flags
 *  - Bundle 120: /admin/incident/finalize-stuck-submissions
 *  - Bundle 123: /admin/incident/migrate-call-tasks
 *  - Bundle 126: /admin/incident/backfill-introductions-from-responses
 *
 * Hygiene: no em/en dashes, no eslint-disable, no photo/image alt.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertTriangle, Play, Beaker, ChevronDown, ChevronRight, Wrench,
} from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import {
  Bundle130BulkReplayBody, Bundle130RescanV2Body,
} from './Bundle130OrphanFlows';
import {
  Bundle131ResurrectHiddenBlocksBody,
  Bundle131BackfillTestimonialLastNamesBody,
  Bundle132RecomputeRatingCountsBody,
  Bundle136HideLegacyTestimonialsBody,
} from './Bundle131_132IncidentFlows';

// Each incident action is declaratively described so a single
// generic modal drives dry-run + apply for all of them.
// Bundle 132 P0-1: Bundle 131 tiles missing on prod. Fixed by
// adding them to this registry + a lint test that catches the
// class of regression forever.
const INCIDENTS = [
  {
    key: 'bundle_131_resurrect_hidden_blocks_with_content',
    bundle: 131,
    label: 'Resurrect hidden blocks with content',
    description: (
      'Fix for events (Philly-shape) where admin edited block '
      + 'content via VBE but never explicitly toggled visibility. '
      + 'Any block with hidden=true AND non-empty content is '
      + 'flipped to hidden=false. Idempotent + audit-logged.'
    ),
    path: '/admin/incident/resurrect-hidden-blocks-with-content',
    fields: [],
    custom: 'bundle131_resurrect_hidden_blocks',
  },
  {
    key: 'bundle_131_backfill_testimonial_last_names',
    bundle: 131,
    label: 'Backfill testimonial last names',
    description: (
      'For every testimonial_consent row missing '
      + 'reporter_last_name, cross-reference the registration + '
      + 'users collection by email. Rebuilds display_name via '
      + 'format_initials when attribution=initials. Idempotent.'
    ),
    path: '/admin/incident/backfill-testimonial-last-names',
    fields: [],
    custom: 'bundle131_backfill_testimonial_last_names',
    superseded: true,
    supersededBy: 'bundle_147_backfill_testimonial_identity_v3',
  },
  {
    key: 'bundle_132_recompute_rating_counts',
    bundle: 132,
    label: 'Recompute rating counts (Alina fix)',
    description: (
      'For events with stale rating_counts_toward_event on rows '
      + 'that were force-completed before Bundle 131. Recomputes '
      + 'via the same _current_is_attendee helper the normal '
      + 'submit path uses. Idempotent + audit-logged.'
    ),
    path: '/admin/incident/recompute-rating-counts',
    fields: [],
    custom: 'bundle132_recompute_rating_counts',
  },
  {
    // Bundle 143 P0-1 CRITICAL recovery for Bundle 136 sweep miss.
    // Reverses `hidden_by_legacy_sweep_at` on any testimonial whose
    // parent survey_response IS submitted via multi-signal join.
    key: 'bundle_143_unhide_wrongly_swept_testimonials',
    bundle: 143,
    label: 'Unhide wrongly-swept testimonials (recovery)',
    description: (
      'Recovery for the Bundle 136 sweep miss where testimonials '
      + 'from pre-Bundle-108 submitted responses (Alina/Ayesha '
      + 'shape) were hidden despite having valid submitted parents. '
      + 'Multi-signal join across response_id + invitation_id + '
      + 'campaign_id + email finds them. Dry-run first, apply on '
      + 'confirm. Idempotent + audit-logged.'
    ),
    path: '/admin/incident/unhide-wrongly-swept-testimonials',
    fields: [{ name: 'event_id', label: 'Event',
                type: 'event-picker', required: false }],
    superseded: true,
    supersededBy: 'bundle_147_unhide_wrongly_swept_testimonials_v3',
  },
  {
    // Bundle 147 P0 - identity hydration for testimonials whose
    // reporter_first / reporter_email fields never got stamped
    // during the Bundle 108 legacy shape. Uses the same graph
    // walker as v2 but scans EVERY row (not just missing-last-name)
    // and hydrates via the invitation chain
    // testimonial -> invitation_id -> survey_responses.registration_id
    // -> event_registrations OR users. Preserves attribution;
    // never overwrites a non-empty existing field.
    key: 'bundle_147_backfill_testimonial_identity_v3',
    bundle: 147,
    label: 'Backfill testimonial identity v3'
      + ' (graph walker + invitation chain)',
    description: (
      'Hydrates reporter_first_name / reporter_last_name / '
      + 'reporter_email for every testimonial row on this event '
      + 'that is missing one or more of them. Preserves attribution '
      + '(initials / first_name / anonymous) verbatim. Never '
      + 'overwrites non-empty existing values. Skips QA seed + '
      + 'is_test rows. Idempotent - re-running is a no-op. Dry-run '
      + 'first.'
    ),
    path: '/admin/incident/backfill-testimonial-identity-v3',
    fields: [{ name: 'event_id', label: 'Event',
                type: 'event-picker', required: true }],
  },
  {
    // Bundle 147 P0 - unhide recovery v3. Predicate fixed:
    // `submitted_at != null` instead of the broken v2 `status IN`
    // check. Explicit guards for private_feedback + qa/test rows
    // so hidden-on-purpose testimonials stay hidden.
    key: 'bundle_147_unhide_wrongly_swept_testimonials_v3',
    bundle: 147,
    label: 'Unhide wrongly-swept testimonials v3'
      + ' (submitted_at predicate + private_feedback guard)',
    description: (
      'v3 recovery for Bundle 136 legacy sweep miss. Bundle 145 '
      + 'v2 shipped a broken predicate that keyed off '
      + '`survey_responses.status IN [submitted, completed]` - '
      + 'every Dallas legacy row falsely returned still_orphan. '
      + 'v3 keys off `submitted_at != null` which is the actual '
      + 'source-of-truth. Explicit private_feedback + qa/test '
      + 'guards ensure legitimately-hidden rows stay hidden.'
    ),
    path: '/admin/incident/unhide-wrongly-swept-testimonials-v3',
    fields: [{ name: 'event_id', label: 'Event',
                type: 'event-picker', required: true }],
  },
  {
    // Bundle 152 P0 - intro workflow forensics. Read-only. GET.
    // Roots the "stuck in identifying" complaint + missing
    // matchmaker manual-complete path. Fix is Bundle 153.
    key: 'bundle_152_intro_workflow_forensics',
    bundle: 152,
    label: 'Intro workflow forensics (read-only)',
    description: (
      'Read-only forensic dump for the reported "stuck in '
      + 'identifying" bug and the missing matchmaker manual-'
      + 'complete workflow (matchmaker made intro via text/DM, '
      + 'needs to close the row). Six sections: registration '
      + 'state-machine with live counts per state and per-'
      + 'transition transition legality, identifying-handler '
      + 'audit that flags there is no UI button surfacing the '
      + 'legal `identifying to identified` transition, top-20 '
      + 'stuck rows PII-masked, admin-UI trace of every page '
      + 'that renders intros with buttons available per state, '
      + 'notification surface trace across every template intro '
      + 'workflow fires, and reporter identity backfill status '
      + 'so ops can see how many stuck rows have hydrateable '
      + 'reporter names. Each section addressable via '
      + '`?section=`. Never mutates data.'
    ),
    path: '/admin/_debug/intro-workflow-forensics',
    method: 'GET',
    applyOnly: true,
    fields: [],
  },
  {
    // Bundle 150 P0 - RSVP flow forensics.
    key: 'bundle_150_rsvp_flow_forensics',
    bundle: 150,
    label: 'RSVP flow forensics (read-only)',
    description: (
      'Read-only forensic dump for the reported RSVP payment '
      + 'recovery bugs: (A) guest submits and skips payment, '
      + 'cannot get back to the payment page; (B) clicking '
      + 'Back on the wizard "completes the RSVP completely"; '
      + '(C) no clickable link to Zelle / Venmo / PayPal that '
      + 'opens a new tab. Five sections: registration state-'
      + 'machine with live counts per state, back-button '
      + 'premature-completion trace with the offending file / '
      + 'line, payment session lifecycle (Stripe TTL, resume '
      + 'link, /account CTA, expiry behavior), manual-transfer '
      + 'scaffolding audit (grep + existing endpoints + admin '
      + 'reconciliation gap), and the event register page '
      + 'state-machine with live per-event method rollup. '
      + 'Each section independently addressable via '
      + '`?section=`. Never mutates data.'
    ),
    path: '/admin/_debug/rsvp-flow-forensics',
    method: 'GET',
    applyOnly: true,
    fields: [],
  },
  {
    // Bundle 148 P0 STRIPE COMPLIANCE - age-gate forensics.
    // Read-only. GET request. Diagnostic-only; no fix in this
    // bundle. Six sections gated by `?section=`.
    key: 'bundle_148_age_gate_forensics',
    bundle: 148,
    label: 'Age-gate forensics (read-only)',
    description: (
      'Read-only forensic dump for the Stripe-flagged incident '
      + 'where a minor DOB can still create a profile. Six '
      + 'sections: DOB entry points, age-gate predicate audit '
      + '(live source snippets from every write path), live '
      + 'under-18 scan across users / event_registrations / '
      + 'pending_registrations, age_gate_terminations write log, '
      + 'DOB mutability trace, and Stripe checkout metadata '
      + 'surface. Each section independently addressable via '
      + '`?section=`. Never mutates data.'
    ),
    path: '/admin/_debug/age-gate-forensics',
    method: 'GET',
    applyOnly: true,
    fields: [],
  },
  {
    // Bundle 146 P0 - stability forensics. Read-only. GET request.
    // Diagnostic-only; no fix ships in this bundle.
    key: 'bundle_146_stability_forensics',
    bundle: 146,
    label: 'Stability forensics (read-only)',
    description: (
      'Read-only forensic dump used to root-cause the prod incident '
      + 'where Motor pool paused mid-session and the pod restarted. '
      + 'Six sections: motor client config, pool state + recent '
      + 'events, health-card timings (each detector wrapped in a '
      + '6s wait_for so no card can stall the diagnostic), liveness '
      + 'probe surface (proves /health does not touch Mongo), '
      + 'recent restart audit, and the exact executionStats plan '
      + 'for the _drill_broken_names query. Each section is '
      + 'independently addressable via `?section=`. Never mutates '
      + 'data.'
    ),
    path: '/admin/_debug/stability-forensics',
    method: 'GET',
    applyOnly: true,
    fields: [],
  },
  {
    // Bundle 145 P0 - forensic read-only dump. GET request.
    // Wired via `method: 'GET'` so runOnce hits it without a body.
    key: 'bundle_145_event_forensics',
    bundle: 145,
    label: 'Event data forensics (read-only)',
    description: (
      'Read-only diagnostic dump for a single event. Prints event '
      + 'doc, campaigns, response counts per join path (event_id, '
      + 'campaign_id), every testimonial with its parent-match '
      + 'signal, introductions, direct connections, and a '
      + 'reporter-last-name source probe. Never mutates data. Use '
      + 'this to diff prod vs preview before running any fix.'
    ),
    path: '/admin/_debug/event-forensics',
    method: 'GET',
    applyOnly: true,
    fields: [{ name: 'event_id', label: 'Event',
                type: 'event-picker', required: true }],
  },
  {
    // Bundle 145 P0-2 - replaces Bundle 143 unhide. Uses the graph
    // walker so testimonials with NULL event_id are found.
    key: 'bundle_145_unhide_wrongly_swept_testimonials_v2',
    bundle: 145,
    label: 'Unhide wrongly-swept testimonials v2 (graph walker)',
    description: (
      'Bundle 145 replacement for the Bundle 143 recovery. Prior '
      + 'enumeration filtered by `testimonial_consents.event_id` '
      + 'only, so rows carrying `campaign_id` or `response_id` '
      + 'with NULL event_id were never scanned. This walks the '
      + 'event -> campaigns -> responses -> testimonials graph so '
      + 'no shape is missed. Dry-run first, apply on confirm.'
    ),
    path: '/admin/incident/unhide-wrongly-swept-testimonials-v2',
    fields: [{ name: 'event_id', label: 'Event',
                type: 'event-picker', required: true }],
    superseded: true,
    supersededBy: 'bundle_147_unhide_wrongly_swept_testimonials_v3',
  },
  {
    // Bundle 145 P0-2 - replaces Bundle 131 backfill. Same graph
    // walker as unhide-v2 so testimonials with NULL event_id land
    // in the scanned set.
    key: 'bundle_145_backfill_testimonial_last_names_v2',
    bundle: 145,
    label: 'Backfill testimonial last names v2 (graph walker)',
    description: (
      'Bundle 145 replacement for the Bundle 131 backfill. Same '
      + 'enumeration bug: rows without event_id were invisible to '
      + 'the pre-145 query. Now walks the campaigns -> responses '
      + '-> testimonials graph and resolves last_name via the '
      + 'shared enrichment helper. Dry-run first.'
    ),
    path: '/admin/incident/backfill-testimonial-last-names-v2',
    fields: [{ name: 'event_id', label: 'Event',
                type: 'event-picker', required: true }],
    superseded: true,
    supersededBy: 'bundle_147_backfill_testimonial_identity_v3',
  },
  {
    // Bundle 140 P0-1 fix - Bundle 136 shipped this endpoint +
    // INCIDENT_REGISTRY entry but the FE tile was never added.
    // Third instance of this "tile ships without button" class of
    // bug. Bundle 140 P0-3 hardens the parity test so this cannot
    // recur.
    key: 'bundle_136_hide_legacy_testimonials',
    bundle: 136,
    label: 'Hide legacy testimonials (orphan sweep)',
    description: (
      'One-time sweep for testimonial_consents whose parent survey '
      + 'response was never submitted. Fixes the Dallas '
      + 'Testimonials-tab-shows-6-when-2-people-submitted class of '
      + 'orphan contamination. Dry-run first, apply on confirm.'
    ),
    path: '/admin/incident/hide-legacy-testimonials',
    fields: [],
    custom: 'bundle136_hide_legacy_testimonials',
  },
  {
    key: 'bundle_130_replay_missed_hooks_for_event',
    bundle: 130,
    label: 'Bulk replay missed intro hooks for one event',
    description: (
      'Backfills the `introductions` collection for every '
      + 'submitted response on the event that had confirmed '
      + 'missed_connections but was submitted under the old '
      + 'Bundle 128 gate (Alina/Ayesha-shape orphans). '
      + 'Idempotent per (response_id, target, when_where).'
    ),
    path: '/admin/incident/replay-missed-hooks-for-event',
    fields: [],
    custom: 'bundle130_bulk_replay',
  },
  {
    key: 'bundle_130_rescan_connection_intent',
    bundle: 130,
    label: 'Rescan connection intent (v2, single response)',
    description: (
      'Bundle 130 replacement for the Bundle 128 rescan. Drops '
      + 'the profile-toggle gate and adds response_id linkage. '
      + 'Idempotent. Pick an event then the specific response.'
    ),
    path: '/admin/incident/rescan-connection-intent-v2',
    fields: [],
    custom: 'bundle130_rescan_v2',
  },
  {
    key: 'bundle_128_survey_connection_audit',
    bundle: 128,
    label: 'Survey to connection audit (read only)',
    description: (
      'Read-only audit: how many survey respondents opted in to '
      + 'be connected in the last N days, and how many produced a '
      + 'downstream introductions or mutual_connections row. '
      + 'Returns the specific response_ids of any gap so an '
      + 'operator can rescan them.'
    ),
    path: '/admin/incident/survey-connection-audit',
    fields: [
      { name: 'days', label: 'Days back (1-365)',
        type: 'text', default: '60' },
    ],
    dryRunOnly: true,
  },
  {
    key: 'bundle_128_rescan_connection_intent',
    bundle: 128,
    label: 'Rescan connection intent for one response',
    description: (
      'Idempotent replay of the missed-connection + direct-exchange '
      + 'downstream hooks for a single survey response id. Use the '
      + 'audit action above to find response_ids in the '
      + '`gap_missed_write_path` list.'
    ),
    path: '/admin/incident/rescan-connection-intent',
    fields: [{ name: 'response_id', label: 'Response id',
                type: 'text', required: true }],
    superseded: true,
    supersededBy: 'bundle_130_rescan_connection_intent',
  },
  {
    key: 'bundle_128_force_complete_response',
    bundle: 128,
    label: 'Force-complete stuck survey response',
    description: (
      'Super-admin only. Marks a stuck in-flight response as '
      + 'submitted and replays every on-submit hook (side effects, '
      + 'rating recompute, direct-exchange follow-up, missed-'
      + 'connection introductions insert). Idempotent. Requires a '
      + 'free-text reason for the audit log.'
    ),
    path: '/admin/incident/force-complete-survey-response',
    fields: [
      { name: 'response_id', label: 'Response id',
        type: 'text', required: true },
      { name: 'reason', label: 'Reason (audit)',
        type: 'text', required: true },
    ],
    applyOnly: true,
    superseded: true,
    supersededBy: 'bundle_129_force_complete_survey_response_v2',
  },
  {
    key: 'bundle_126_backfill_intros',
    bundle: 126,
    label: 'Backfill introductions from responses',
    description: (
      'Repairs the pre-Bundle 126 data-loss bug where survey '
      + 'submits wrote missed_connections rows to the dead-end '
      + 'missed_connection_requests collection instead of '
      + 'introductions. Idempotent per '
      + '(event, reporter, target, when_where).'
    ),
    path: '/admin/incident/backfill-introductions-from-responses',
    fields: [{ name: 'event_id', label: 'Event',
                type: 'event-picker', required: true }],
  },
  {
    key: 'bundle_123_migrate_call_tasks',
    bundle: 123,
    label: 'Migrate legacy call task rows',
    description: (
      'Migrates rows from introduction_call_tasks into '
      + 'introduction_calls. Read-only for callers; the source '
      + 'row is stamped with bundle_123_archived_at.'
    ),
    path: '/admin/incident/migrate-call-tasks',
    fields: [],
  },
  {
    key: 'bundle_120_finalize_stuck_submissions',
    bundle: 120,
    label: 'Finalize stuck survey submissions',
    description: (
      'Marks in-flight survey_responses rows that have all '
      + 'required answers but never reached submitted_at. Requires '
      + 'the target event to have exactly one active campaign.'
    ),
    path: '/admin/incident/finalize-stuck-submissions',
    fields: [{ name: 'event_id', label: 'Event',
                type: 'event-picker', required: true }],
  },
  {
    key: 'bundle_119_reset_drift',
    bundle: 119,
    label: 'Reset response schema-drift flags',
    description: (
      'Clears the schema-drift banner on survey_responses when the '
      + 'question set has been refreshed and the responses are '
      + 'again valid against the current version.'
    ),
    path: '/admin/incident/reset-response-drift-flags',
    fields: [{ name: 'event_id', label: 'Event',
                type: 'event-picker', required: true }],
  },
  {
    key: 'bundle_117_reset_reg_state',
    bundle: 117,
    label: 'Reset registration survey state',
    description: (
      'Clears the per-registration cached survey state so a '
      + 'stuck respondent can restart the survey without losing '
      + 'their submitted answers on other events.'
    ),
    path: '/admin/incident/reset-registration-survey-state',
    fields: [{ name: 'registration_id', label: 'Registration id',
                type: 'text', required: true }],
  },
  {
    key: 'bundle_116_refresh_qset',
    bundle: 116,
    label: 'Refresh question set',
    description: (
      'Regenerates the sanitized question_set on the target '
      + 'campaign from the standard builder. Existing responses '
      + 'are untouched.'
    ),
    path: '/admin/incident/refresh-question-set',
    fields: [{ name: 'event_id', label: 'Event',
                type: 'event-picker', required: true }],
  },
  {
    key: 'bundle_112_reset_stale',
    bundle: 112,
    label: 'Reset stale invitations',
    description: (
      'Re-invalidates 30+ recipients whose invitations point at '
      + 'archived campaigns after the Bundle 110 dedupe tiebreak '
      + 'chose the wrong side. Send Live will re-issue them.'
    ),
    path: '/admin/incident/reset-stale-invitations',
    fields: [{ name: 'event_id', label: 'Event',
                type: 'event-picker', required: true }],
  },
  {
    key: 'bundle_110_dedupe_invitations',
    bundle: 110,
    label: 'Dedupe invitations for campaign',
    description: (
      'Deduplicates survey_invitations and survey_responses for a '
      + 'single campaign, keeping the most recent send. Run this '
      + 'after Bundle 109 consolidation.'
    ),
    path: '/admin/incident/dedupe-invitations',
    fields: [
      { name: 'event_id', label: 'Event', type: 'event-picker',
        required: true },
      { name: 'campaign_id', label: 'Campaign id', type: 'text',
        required: true },
    ],
  },
  {
    key: 'bundle_109_consolidate',
    bundle: 109,
    label: 'Consolidate duplicate campaigns',
    description: (
      'Keeps the oldest campaign on the event and archives the '
      + 'duplicates, re-pointing every child row onto the keeper.'
    ),
    path: '/admin/incident/consolidate-campaigns',
    fields: [{ name: 'event_id', label: 'Event',
                type: 'event-picker', required: true }],
  },
  {
    key: 'bundle_108_backfill_reissue',
    bundle: 108,
    label: 'Backfill Bundle 107 reissue logs',
    description: (
      'Attaches survey_reissue and legacy_survey_apology email_logs '
      + 'back to the KEEP campaign after the Bundle 109 '
      + 'consolidation reshuffle.'
    ),
    path: '/admin/incident/backfill-bundle107-reissue',
    fields: [{ name: 'event_id', label: 'Event',
                type: 'event-picker', required: true }],
  },
];

const IncidentActionsPanel = () => {
  const [open, setOpen] = useState(false);
  const [activeIncident, setActiveIncident] = useState(null);
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [events, setEvents] = useState([]);
  const [showAll, setShowAll] = useState(false);
  // Bundle 159 - show/hide superseded tiles. Off by default so
  // the grid stays lean; toggle reveals the muted rows.
  const [showSuperseded, setShowSuperseded] = useState(false);

  useEffect(() => {
    // Cheap events list for the event-picker.
    api.get('/admin/events?limit=200').then(({ data }) => {
      setEvents(data.events || data || []);
    }).catch(() => setEvents([]));
  }, []);

  const startIncident = (incident) => {
    setActiveIncident(incident);
    setValues({});
    setLastResult(null);
    setOpen(true);
  };

  const runOnce = async (dryRun) => {
    if (!activeIncident) return;
    // Validate required fields.
    for (const f of activeIncident.fields) {
      if (f.required && !values[f.name]) {
        toast.error(`Please provide ${f.label}.`);
        return;
      }
    }
    setBusy(true);
    try {
      let data;
      if ((activeIncident.method || 'POST').toUpperCase() === 'GET') {
        // Bundle 145 - GET-shaped incident endpoints (forensics).
        // Bundle 158 - only append the event_id path segment when
        // the tile actually declares a field. Read-only forensics
        // registered by Bundles 146 / 148 / 150 / 152 do NOT take
        // a path param; appending `/${''}` yielded a trailing
        // slash + 404 for those tiles.
        const hasPathParam = Array.isArray(activeIncident.fields)
          && activeIncident.fields.length > 0;
        const evId = values.event_id || '';
        const url = hasPathParam
          ? `${activeIncident.path}/${encodeURIComponent(evId)}`
          : activeIncident.path;
        const resp = await api.get(url);
        data = resp.data;
      } else {
        const body = { dry_run: dryRun, ...values };
        const resp = await api.post(activeIncident.path, body);
        data = resp.data;
      }
      setLastResult({ dry_run: dryRun, data });
      toast.success(dryRun ? 'Dry-run complete.' : 'Applied.');
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || `Incident action failed: ${e.message || 'unknown'}`);
    } finally { setBusy(false); }
  };

  // Bundle 159 - sort primary tiles by bundle marker DESC so the
  // latest ship lands top-left. Legacy (<120) stays in the
  // collapsable at the bottom.
  const supersededByKey = INCIDENTS.reduce((acc, i) => {
    acc[i.key] = i; return acc;
  }, {});
  const supersededCount = INCIDENTS
    .filter((i) => i.superseded).length;
  const primaryAll = INCIDENTS
    .filter((i) => i.bundle >= 120)
    .slice()
    .sort((a, b) => b.bundle - a.bundle);
  const primary = showSuperseded
    ? primaryAll
    : primaryAll.filter((i) => !i.superseded);
  const secondary = INCIDENTS.filter((i) => i.bundle < 120);

  const canApply = !!activeIncident?.applyOnly
    || (lastResult && lastResult.dry_run);
  const canDryRun = !activeIncident?.applyOnly;

  return (
    <Card
      className="border-primary-200 bg-primary-50/40"
      data-testid="incident-actions-panel"
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-primary-700" />
            <div>
              <div className="font-semibold text-primary-900">
                Incident actions
              </div>
              <div className="text-xs text-neutral-600">
                Every recovery endpoint gets a button. Dry-run is the
                default, Apply is idempotent. Latest bundles first.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setShowSuperseded((s) => !s)}
              className="text-xs text-secondary-700 hover:text-secondary-900 inline-flex items-center gap-1"
              data-testid="incident-toggle-superseded"
              aria-pressed={showSuperseded}
            >
              {showSuperseded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              {showSuperseded ? 'Hide superseded' : 'Show superseded'} actions ({supersededCount})
            </button>
            <button
              type="button"
              onClick={() => setShowAll((s) => !s)}
              className="text-xs text-secondary-700 hover:text-secondary-900 inline-flex items-center gap-1"
              data-testid="incident-toggle-legacy"
            >
              {showAll ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              {showAll ? 'Hide legacy (108-119)' : 'Show legacy (108-119)'}
            </button>
          </div>
        </div>
        <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
          {primary.map((i) => (
            <IncidentButton key={i.key} incident={i}
                             replacement={i.supersededBy
                               ? supersededByKey[i.supersededBy]
                               : null}
                             onStart={startIncident} />
          ))}
        </div>
        {showAll && (
          <div className="pt-2 border-t border-primary-100">
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">
              Legacy incidents
            </div>
            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {secondary.map((i) => (
                <IncidentButton key={i.key} incident={i}
                                 onStart={startIncident} />
              ))}
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={(o) => { if (!o) setOpen(false); }}>
        <DialogContent className="max-w-2xl"
                        data-testid="incident-modal">
          {activeIncident && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  {activeIncident.label}
                  <Badge variant="outline" className="ml-2 text-[10px]">
                    Bundle {activeIncident.bundle}
                  </Badge>
                </DialogTitle>
                <DialogDescription>
                  {activeIncident.description}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3 py-2">
                {activeIncident.custom === 'bundle130_bulk_replay' ? (
                  <Bundle130BulkReplayBody />
                ) : activeIncident.custom === 'bundle130_rescan_v2' ? (
                  <Bundle130RescanV2Body />
                ) : activeIncident.custom === 'bundle131_resurrect_hidden_blocks' ? (
                  <Bundle131ResurrectHiddenBlocksBody />
                ) : activeIncident.custom === 'bundle131_backfill_testimonial_last_names' ? (
                  <Bundle131BackfillTestimonialLastNamesBody />
                ) : activeIncident.custom === 'bundle132_recompute_rating_counts' ? (
                  <Bundle132RecomputeRatingCountsBody />
                ) : activeIncident.custom === 'bundle136_hide_legacy_testimonials' ? (
                  <Bundle136HideLegacyTestimonialsBody />
                ) : (
                  <>
                    {activeIncident.fields.map((f) => (
                      <div key={f.name} className="space-y-1">
                        <label className="text-xs uppercase tracking-widest text-neutral-500">
                          {f.label}{f.required ? ' *' : ''}
                        </label>
                        {f.type === 'event-picker' ? (
                          <Select
                            value={values[f.name] || ''}
                            onValueChange={(v) => setValues((s) => ({ ...s, [f.name]: v }))}
                          >
                            <SelectTrigger data-testid={`incident-field-${f.name}`}>
                              <SelectValue placeholder="Choose an event" />
                            </SelectTrigger>
                            <SelectContent>
                              {events.map((e) => (
                                <SelectItem key={e.id} value={e.id}>
                                  {e.title || e.id} ({(e.event_date || e.start_datetime || '').slice(0, 10)})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            value={values[f.name] || ''}
                            onChange={(e) => setValues((s) => ({ ...s, [f.name]: e.target.value }))}
                            placeholder={f.label}
                            data-testid={`incident-field-${f.name}`}
                          />
                        )}
                      </div>
                    ))}

                    {lastResult && (
                      <div
                        className="mt-3 rounded border border-neutral-200 bg-white p-3 max-h-64 overflow-auto"
                        data-testid="incident-result"
                      >
                        <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1">
                          {lastResult.dry_run ? 'Dry-run result' : 'Apply result'}
                        </div>
                        <pre className="text-[11px] font-mono text-primary-900 whitespace-pre-wrap">
                          {JSON.stringify(lastResult.data, null, 2)}
                        </pre>
                      </div>
                    )}
                  </>
                )}
              </div>

              {activeIncident.custom ? (
                <DialogFooter className="flex justify-end sm:justify-end">
                  <div className="text-[11px] text-neutral-500 mr-auto">
                    Super admin only. Dry-run first, then Apply.
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setOpen(false)}
                    data-testid="incident-custom-close-btn"
                  >
                    Close
                  </Button>
                </DialogFooter>
              ) : (
                <DialogFooter className="flex gap-2 justify-between sm:justify-between">
                  <div className="text-[11px] text-neutral-500">
                    Super admin only. Dry-run first.
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      disabled={busy || !canDryRun}
                      onClick={() => runOnce(true)}
                      data-testid="incident-dry-run-btn"
                    >
                      <Beaker className="h-4 w-4 mr-1.5" /> Dry-run
                    </Button>
                    <Button
                      disabled={busy || !canApply}
                      onClick={() => runOnce(false)}
                      className="bg-primary-800 text-white hover:bg-primary-900"
                      data-testid="incident-apply-btn"
                    >
                      <Play className="h-4 w-4 mr-1.5" /> Apply
                    </Button>
                  </div>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const IncidentButton = ({ incident, onStart, replacement }) => {
  const isSuperseded = !!incident.superseded;
  return (
    <button
      type="button"
      onClick={() => onStart(incident)}
      className={
        'text-left rounded-lg border p-3 shadow-sm hover:shadow-md transition-all '
        + (isSuperseded
          ? 'border-neutral-200 bg-neutral-50/70 opacity-70 hover:opacity-90'
          : 'border-neutral-200 hover:border-primary-400 bg-white')
      }
      data-testid={`incident-btn-${incident.key}`}
      data-superseded={isSuperseded ? 'true' : 'false'}
    >
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <Badge variant="outline" className="text-[9px] uppercase tracking-widest">
          Bundle {incident.bundle}
        </Badge>
        {isSuperseded ? (
          <span
            className="text-[9px] uppercase tracking-widest font-semibold text-amber-800 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5"
            data-testid={`incident-superseded-chip-${incident.key}`}
            title={replacement
              ? `Use ${replacement.label}`
              : 'Superseded by a newer action'}
          >
            {replacement
              ? `SUPERSEDED · use ${replacement.label}`
              : 'SUPERSEDED'}
          </span>
        ) : null}
      </div>
      <div className={
        'text-sm font-semibold '
        + (isSuperseded ? 'text-neutral-600' : 'text-primary-900')
      }>
        {incident.label}
      </div>
      <div className="text-[11px] text-neutral-500 line-clamp-2 mt-0.5">
        {incident.description}
      </div>
    </button>
  );
};

export { INCIDENTS };
export default IncidentActionsPanel;
